<template>
  <span class="inline-block">
    <div
        @mouseenter="showTooltip"
        @mouseleave="hideTooltip"
        @focus="showTooltip"
        @blur="hideTooltip"
        class="cursor-pointer"
        :class="triggerClass"
        ref="triggerRef"
    >
      <slot name="trigger">
        <slot>
          <IconTooltip class="hover:text-blue-800" />
        </slot>
      </slot>
    </div>
    
    <Teleport to="body">
      <div
          v-if="isVisible"
          ref="tooltipRef"
          @mouseenter="onTooltipMouseEnter"
          @mouseleave="onTooltipMouseLeave"
          :class="[
            'fixed px-4 py-3 text-sm font-medium rounded-3xl shadow-2xl border tooltip-container ',
            themeClasses,
            customClass,
            { 'overflow-y-auto': shouldShowScroll },
          ]"
          :style="{
            top: tooltipTop + 'px',
            left: tooltipLeft + 'px',
            maxWidth: '800px',
            maxHeight: shouldShowScroll ? '300px' : 'auto',
            zIndex: 99999,
            ...customStyles
          }"
          role="tooltip"
      >
        <div v-if="$slots.content" class="tooltip-content relative z-10">
          <slot name="content"></slot>
        </div>
        <div v-else-if="title" v-html="title" class="tooltip-content relative z-10"></div>
        <div v-else class="tooltip-content relative z-10">
          <slot name="tooltip"></slot>
        </div>
      </div>
    </Teleport>
  </span>
</template>

<script setup lang="ts">
import { ref, computed, nextTick, watch } from 'vue'
import IconTooltip from '@/components/svg_icons/IconTooltip.vue'

interface Props {
  title?: string
  placement?: 'top' | 'bottom' | 'left' | 'right' | 'top-start' | 'top-end' | 'bottom-start' | 'bottom-end'
  delay?: number
  hideDelay?: number
  disabled?: boolean
  triggerClass?: string
  customClass?: string
  customStyles?: Record<string, string>
  maxWidth?: string
  theme?: 'dark' | 'light' | 'primary' | 'success' | 'warning' | 'error' | 'yellow' | 'blue'
}

const props = withDefaults(defineProps<Props>(), {
  placement: 'top',
  delay: 100,
  hideDelay: 100,
  disabled: false,
  triggerClass: '',
  customClass: '',
  maxWidth: '400px',
  theme: 'dark'
})

const isVisible = ref(false)
const triggerRef = ref<HTMLElement>()
const tooltipRef = ref<HTMLElement>()
const isHoveringTooltip = ref(false)
const isHoveringTrigger = ref(false)
let showTimeout: NodeJS.Timeout | null = null
let hideTimeout: NodeJS.Timeout | null = null

// Adaptive positioning logic
const adaptivePosition = ref(props.placement)
const adaptiveMaxWidth = ref(props.maxWidth)

// New simplified positioning logic
const tooltipTop = ref(0)
const tooltipLeft = ref(0)

// Check if content height exceeds 300px to determine if scroll is needed
const shouldShowScroll = ref(false)
const shouldWrap = ref(false)

const updateTooltipPosition = () => {
  if (!triggerRef.value || !isVisible.value) return

  const triggerRect = triggerRef.value.getBoundingClientRect()
  const tooltipRect = tooltipRef.value?.getBoundingClientRect() || { height: 0, width: 0 }

  if (tooltipRect.width >= 400) {
    shouldWrap.value = true
  } else {
    shouldWrap.value = false
  }

  const offset = 12
  let finalTop = 0
  let finalLeft = 0

  switch (props.placement) {
    case 'top':
      finalTop = triggerRect.top - tooltipRect.height - offset
      finalLeft = triggerRect.left + triggerRect.width / 2 - tooltipRect.width / 2
      break
    case 'bottom':
      finalTop = triggerRect.bottom + offset
      finalLeft = triggerRect.left + triggerRect.width / 2 - tooltipRect.width / 2
      break
    case 'left':
      finalTop = triggerRect.top + triggerRect.height / 2 - tooltipRect.height / 2
      finalLeft = triggerRect.left - tooltipRect.width - offset
      break
    case 'right':
      finalTop = triggerRect.top + triggerRect.height / 2 - tooltipRect.height / 2
      finalLeft = triggerRect.right + offset
      break
    case 'top-start':
      finalTop = triggerRect.top - tooltipRect.height - offset
      finalLeft = triggerRect.left
      break
    case 'top-end':
      finalTop = triggerRect.top - tooltipRect.height - offset
      finalLeft = triggerRect.right - tooltipRect.width
      break
    case 'bottom-start':
      finalTop = triggerRect.bottom + offset
      finalLeft = triggerRect.left
      break
    case 'bottom-end':
      finalTop = triggerRect.bottom + offset
      finalLeft = triggerRect.right - tooltipRect.width
      break
  }

  const viewportWidth = window.innerWidth
  const viewportHeight = window.innerHeight
  const margin = 10

  const wouldOverlap = (top: number, left: number) => {
    const tooltipRight = left + tooltipRect.width
    const tooltipBottom = top + tooltipRect.height

    return !(
      left > triggerRect.right ||
      tooltipRight < triggerRect.left ||
      top > triggerRect.bottom ||
      tooltipBottom < triggerRect.top
    )
  }

  if (wouldOverlap(finalTop, finalLeft)) {
    const alternatives = [
      {
        top: props.placement.includes('top') ? triggerRect.bottom + offset : triggerRect.top - tooltipRect.height - offset,
        left: triggerRect.left + triggerRect.width / 2 - tooltipRect.width / 2
      },
      {
        top: triggerRect.top + triggerRect.height / 2 - tooltipRect.height / 2,
        left: triggerRect.right + offset
      },
      {
        top: triggerRect.top + triggerRect.height / 2 - tooltipRect.height / 2,
        left: triggerRect.left - tooltipRect.width - offset
      }
    ]

    for (const alt of alternatives) {
      if (!wouldOverlap(alt.top, alt.left) &&
          alt.left >= margin &&
          alt.left + tooltipRect.width <= viewportWidth - margin &&
          alt.top >= margin &&
          alt.top + tooltipRect.height <= viewportHeight - margin) {
        finalTop = alt.top
        finalLeft = alt.left
        break
      }
    }
  }

  if (finalLeft < margin) {
    finalLeft = margin
  } else if (finalLeft + tooltipRect.width > viewportWidth - margin) {
    finalLeft = viewportWidth - tooltipRect.width - margin
  }

  if (finalTop < margin) {
    finalTop = margin
  } else if (finalTop + tooltipRect.height > viewportHeight - margin) {
    finalTop = viewportHeight - tooltipRect.height - margin
  }

  if (wouldOverlap(finalTop, finalLeft)) {
    finalTop = Math.max(triggerRect.bottom + offset, margin)
    if (finalTop + tooltipRect.height > viewportHeight - margin) {
      finalTop = Math.max(triggerRect.top - tooltipRect.height - offset, margin)
    }
  }

  tooltipTop.value = finalTop
  tooltipLeft.value = finalLeft
}

const checkContentHeight = () => {
  if (!tooltipRef.value) return

  const tooltip = tooltipRef.value
  const originalMaxHeight = tooltip.style.maxHeight
  const originalOverflow = tooltip.style.overflowY

  tooltip.style.maxHeight = 'none'
  tooltip.style.overflowY = 'visible'

  const contentHeight = tooltip.scrollHeight

  tooltip.style.maxHeight = originalMaxHeight
  tooltip.style.overflowY = originalOverflow

  shouldShowScroll.value = contentHeight > 300
}

const positionClasses = computed(() => {
  const positions = {
    'top': 'bottom-full left-1/2 transform -translate-x-1/2 mb-2',
    'bottom': 'top-full left-1/2 transform -translate-x-1/2 mt-2',
    'left': 'right-full top-1/2 transform -translate-y-1/2 mr-2',
    'right': 'left-full top-1/2 transform -translate-y-1/2 ml-2',
    'top-start': 'bottom-full left-0 mb-2',
    'top-end': 'bottom-full right-0 mb-2',
    'bottom-start': 'top-full left-0 mt-2',
    'bottom-end': 'top-full right-0 mt-2'
  }
  return positions[adaptivePosition.value]
})

const arrowClasses = computed(() => {
  const baseArrows = {
    'top': 'absolute top-full left-1/2 transform -translate-x-1/2 border-l-4 border-r-4 border-t-4 border-transparent',
    'bottom': 'absolute bottom-full left-1/2 transform -translate-x-1/2 border-l-4 border-r-4 border-b-4 border-transparent',
    'left': 'absolute left-full top-1/2 transform -translate-y-1/2 border-t-4 border-b-4 border-l-4 border-transparent',
    'right': 'absolute right-full top-1/2 transform -translate-y-1/2 border-t-4 border-b-4 border-r-4 border-transparent',
    'top-start': 'absolute top-full left-4 border-l-4 border-r-4 border-t-4 border-transparent',
    'top-end': 'absolute top-full right-4 border-l-4 border-r-4 border-t-4 border-transparent',
    'bottom-start': 'absolute bottom-full left-4 border-l-4 border-r-4 border-b-4 border-transparent',
    'bottom-end': 'absolute bottom-full right-4 border-l-4 border-r-4 border-b-4 border-transparent'
  }

  const arrowColors = {
    'dark': 'border-t-gray-900 border-b-gray-900 border-l-gray-900 border-r-gray-900',
    'light': 'border-t-white border-b-white border-l-white border-r-white',
    'primary': 'border-t-blue-800 border-b-blue-800 border-l-blue-800 border-r-blue-800',
    'success': 'border-t-green-600 border-b-green-600 border-l-green-600 border-r-green-600',
    'warning': 'border-t-yellow-600 border-b-yellow-600 border-l-yellow-600 border-r-yellow-600',
    'error': 'border-t-red-600 border-b-red-600 border-l-red-600 border-r-red-600',
    'yellow': 'border-t-yellow-200 border-b-yellow-200 border-l-yellow-200 border-r-yellow-200',
    'blue': 'border-t-blue-200 border-b-blue-200 border-l-blue-200 border-r-blue-200'
  }

  return `${baseArrows[adaptivePosition.value]} ${arrowColors[props.theme]}`
})

const themeClasses = computed(() => {
  const themes = {
    'dark': 'bg-gray-900 text-white',
    'light': 'bg-white text-gray-900',
    'primary': 'bg-blue-800 text-white',
    'success': 'bg-green-600 text-white',
    'warning': 'bg-yellow-600 text-white',
    'error': 'bg-red-600 text-white',
    'yellow': 'bg-yellow-100 text-gray-900',
    'blue': 'bg-blue-100 text-gray-900'
  }
  return themes[props.theme]
})

// Hover management for persistent tooltip
const onTooltipMouseEnter = () => {
  isHoveringTooltip.value = true
  if (hideTimeout) {
    clearTimeout(hideTimeout)
    hideTimeout = null
  }
}

const onTooltipMouseLeave = () => {
  isHoveringTooltip.value = false
  if (!isHoveringTrigger.value) {
    startHideTimer()
  }
}

const startHideTimer = () => {
  if (hideTimeout) {
    clearTimeout(hideTimeout)
  }

  hideTimeout = setTimeout(() => {
    if (!isHoveringTrigger.value && !isHoveringTooltip.value) {
      isVisible.value = false
    }
  }, props.hideDelay)
}

const showTooltip = async () => {
  if (props.disabled) return

  isHoveringTrigger.value = true

  if (hideTimeout) {
    clearTimeout(hideTimeout)
    hideTimeout = null
  }

  if (showTimeout) {
    clearTimeout(showTimeout)
  }
  isVisible.value = true

  nextTick(() => {
    updateTooltipPosition()
    checkContentHeight()
  })
}

const hideTooltip = () => {
  if (props.disabled) return

  isHoveringTrigger.value = false

  if (showTimeout) {
    clearTimeout(showTimeout)
    showTimeout = null
  }

  if (!isHoveringTooltip.value) {
    startHideTimer()
  }
}

defineExpose({
  show: showTooltip,
  hide: hideTooltip,
  toggle: () => isVisible.value ? hideTooltip() : showTooltip()
})

watch([() => props.placement, () => props.maxWidth], () => {
  adaptivePosition.value = props.placement
  adaptiveMaxWidth.value = props.maxWidth
  nextTick(updateTooltipPosition)
})

window.addEventListener('resize', () => {
  nextTick(updateTooltipPosition)
})

nextTick(updateTooltipPosition)
</script>

<style scoped>
.tooltip-content {
  line-height: 1.4;
  word-wrap: break-word;
  overflow-wrap: break-word;
}

/* Force tooltip to respect max-width constraint */
.tooltip-container {
  width: fit-content;
  max-width: 400px !important;
  box-sizing: border-box;
}

.tooltip-content :deep(a) {
  @apply text-blue-300 hover:text-blue-200 underline;
}

.tooltip-content :deep(strong) {
  @apply font-semibold;
}

.tooltip-content :deep(.tooltip-icon) {
  @apply inline-block w-4 h-4 mr-1;
}

.tooltip-content :deep(.tooltip-badge) {
  @apply inline-block px-2 py-1 text-xs rounded-full bg-blue-600 text-white mr-1;
}

.tooltip-content :deep(ul) {
  @apply list-disc list-inside space-y-1 mt-1;
}

.tooltip-content :deep(li) {
  @apply text-sm;
}

/* Theme variations */
.tooltip-light {
  @apply bg-white text-gray-900 border border-gray-200 shadow-lg;
}

.tooltip-primary {
  @apply bg-blue-600 text-white;
}

.tooltip-success {
  @apply bg-green-600 text-white;
}

.tooltip-warning {
  @apply bg-yellow-600 text-white;
}

.tooltip-error {
  @apply bg-red-600 text-white;
}
</style>
